import mixpanel from "mixpanel-browser";

import { isTrackingDisabled } from ".";

export const isMixpanelEnabled = () => {
  return (
    import.meta.env.VITE_APP_MIXPANEL_TOKEN &&
    import.meta.env.MODE !== "development" &&
    (import.meta.env.MODE === "production" || !isTrackingDisabled())
  );
};

/**
 * Registers super properties in Mixpanel.
 * Super properties are properties that are automatically included in every event.
 *
 * @param {Object} user - The user object containing user information.
 * @param {string} user.email - The user's email address.
 * @param {string} user.name - The user's full name.
 * @param {string} user.role - The user's role.
 * @param {Object} user.oem - The OEM information.
 * @param {string} user.oem.name - The OEM name.
 */
export const registerMixpanelSuperProperties = (user) => {
  if (!isMixpanelEnabled() || !user?._id) return;

  const superProperties = {
    userEmail: user?.email,
    userName: user?.name,
    oemName: user?.oem?.name,
    userRole: user?.role,
  };

  // Register super properties that will be sent with every event
  mixpanel.register(superProperties);
};

/**
 * Unregisters super properties from Mixpanel.
 * This should be called on logout to clear user-specific data.
 */
export const unregisterMixpanelSuperProperties = () => {
  if (!isMixpanelEnabled()) return;

  // Unregister the super properties
  mixpanel.unregister("userEmail");
  mixpanel.unregister("userName");
  mixpanel.unregister("oemName");
  mixpanel.unregister("userRole");
};

/**
 * Tracks an event in both Mixpanel and Intercom.
 *
 * Ensures the event is logged in Mixpanel first. If Intercom is available in the window,
 * it also logs the same event in Intercom.
 *
 * @param {string} event - The name of the event to track.
 * @param {Record<string, any>} [properties={}] - Optional event properties.
 * @returns {Promise<void>} - Returns a promise that resolves after tracking.
 */
export const registerMixpanelEvent = async (event, properties = {}) => {
  if (isMixpanelEnabled()) {
    mixpanel.track(event, { ...properties });

    if (window.Intercom) {
      window.Intercom("trackEvent", event, properties);
    }
  }
};
